import { all, apply, put, takeLatest } from 'redux-saga/effects';

import { getSingleton } from '@occc-app/Activate/api';
import { Audience as AudienceAPI, Segment as SegmentAPI } from '@occc-app/Activate/apiTypes';
import { safeAsync } from '@occc-app/lib/safeAsync';
import { ClientError } from '@occc-app/lib/client';

import actions from './actions';
import types from './types';
import { deserializeAudience, deserializeAudienceType, deserializeSegments } from './deserializers';

import { types as offerTypes, ActionCreators as OfferActionCreators } from '../../../redux';

// Request Audiences
function* requestAudience(id: string, apiAudienceType: AudienceAPI['attributes']['audience_type']) {
  const core = getSingleton();
  try {
    const audienceType = deserializeAudienceType(apiAudienceType);
    yield put(actions.requestAudience(id, audienceType));

    const audienceData = (yield apply(core, core.audiences.id(id).show, [{}])) as {
      data: AudienceAPI;
    };

    const selectedAudience = deserializeAudience(audienceData.data);
    yield put(actions.requestAudienceOK(selectedAudience));
    yield put(actions.requestSegments());
  } catch (error) {
    const errorMessages = error.errorMessages || [
      {
        message: `Unable to request audience: ${apiAudienceType}`,
      },
    ];
    yield put(actions.requestAudienceERR(errorMessages, error));
    throw error;
  }
}

function* requestAudiences(action: ReturnType<OfferActionCreators['RequestOfferOK']>) {
  yield all(
    action.audiences.map((audience) =>
      requestAudience(audience.id!, audience.attributes.audience_type),
    ),
  );
  yield put(actions.requestAudiencesOK());
}

// Request Segments
function* requestSegments() {
  const core = getSingleton();
  const segmentsData = (yield apply(core, core.segments.index, [{}])) as {
    data: SegmentAPI[];
  };

  const segments = deserializeSegments(segmentsData.data);
  yield put(actions.requestSegmentsOK(segments));
}

const sagas = {
  requestAudiences: safeAsync(requestAudiences),
  requestSegments: safeAsync(requestSegments, (error: ClientError) => {
    const errorMessages = error.errorMessages || [{ message: 'Unable to request segments.' }];
    return actions.requestAudienceERR(errorMessages, error);
  }),
};

export default function* saga() {
  yield takeLatest(offerTypes.REQUEST_OFFER_OK, sagas.requestAudiences);
  yield takeLatest(types.REQUEST_SEGMENTS, sagas.requestSegments);
}
